import { fetchWithAuth } from '../../../common/utils/apiUtils';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api';

class DepartmentService {

    getMvtUrlTemplate() {
        const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api';
        return `${apiBaseUrl}/mvt/departments/{z}/{x}/{y}`;
    }

    async getAllDepartmentsGeoJson() {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/departments/geojson`);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            return { data };
        } catch (error) {
            console.error('Error fetching departments GeoJSON:', error);
            throw error;
        }
    }

    /**
     * Obtener todos los departamentos
     * @returns {Promise} Lista de departamentos con sus geometrías
     */
    async getAllDepartments() {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/departments`);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error fetching departments:', error);
            throw error;
        }
    }

    /**
     * Obtener un departamento por ID
     * @param {number} id - ID del departamento
     * @returns {Promise} Departamento específico
     */
    async getDepartmentById(id) {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/departments/${id}`);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error fetching department by ID:', error);
            throw error;
        }
    }

    /**
     * Obtener un departamento por nombre
     * @param {string} name - Nombre del departamento
     * @returns {Promise} Departamento específico
     */
    async getDepartmentByName(name) {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/departments/name/${encodeURIComponent(name)}`);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error fetching department by name:', error);
            throw error;
        }
    }

    /**
     * Subir archivo GeoJSON con departamentos
     * @param {Object} geoJsonData - Objeto GeoJSON completo
     * @returns {Promise} Respuesta con departamentos guardados
     */
    async uploadGeoJson(geoJsonData) {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/departments/upload`, {
                method: 'POST',
                body: JSON.stringify(geoJsonData)
            });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            return { data };
        } catch (error) {
            console.error('Error uploading GeoJSON:', error);
            throw error;
        }
    }

    /**
     * Eliminar todos los departamentos
     * @returns {Promise} Confirmación de eliminación
     */
    async deleteAllDepartments() {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/departments`, {
                method: 'DELETE'
            });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error deleting departments:', error);
            throw error;
        }
    }

    /**
     * Convertir departamentos de Spring Boot a formato GeoJSON
     * para usar con librerías de mapas como Leaflet o MapboxGL
     * @param {Array} departments - Array de departamentos desde el backend
     * @returns {Object} GeoJSON FeatureCollection
     */
    convertToGeoJSON(departments) {
        return {
            type: 'FeatureCollection',
            features: departments.map(dept => ({
                type: 'Feature',
                properties: {
                    id: dept.id,
                    name: dept.name
                },
                geometry: this.convertJTSToGeoJSON(dept.geometry)
            }))
        };
    }

    /**
     * Convertir geometría JTS del backend a formato GeoJSON
     * @param {Object} jtsGeometry - Geometría en formato JTS desde Spring Boot
     * @returns {Object} Geometría en formato GeoJSON
     */
    convertJTSToGeoJSON(jtsGeometry) {
        // Spring Boot con Jackson serializa JTS geometry directamente a GeoJSON
        // pero en caso de que venga en otro formato, esta función lo normaliza
        if (jtsGeometry.type && jtsGeometry.coordinates) {
            return jtsGeometry;
        }

        // Si viene en formato diferente, adaptarlo aquí
        return {
            type: jtsGeometry.geometryType || 'MultiPolygon',
            coordinates: jtsGeometry.coordinates
        };
    }
}

export default new DepartmentService();